import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getDashboardData } from '../../services/adminService';
import { AdminDashboardData, SubmittedReport } from '../../types/admin';
import { ReportViewModal } from '../ReportViewModal';
import './AdminDashboard.css';
import { getRiskLevelClass, getRiskLevelText, getTargetGroupText } from '../../utils/surveyUtils';

export const AdminDashboard: React.FC = () => {
	const { token } = useAuth();
	const [dashboardData, setDashboardData] = useState<AdminDashboardData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string>('');
	const [selectedReport, setSelectedReport] = useState<SubmittedReport | null>(null);
	const [isReportModalOpen, setIsReportModalOpen] = useState(false);

	const loadDashboardData = useCallback(async () => {
		if (!token) return;

		try {
			setLoading(true);
			setError('');
			const data = await getDashboardData(token);
			setDashboardData(data);
		} catch (err) {
			setError('Greška pri učitavanju podataka');
			console.error('Dashboard error:', err);
		} finally {
			setLoading(false);
		}
	}, [token]);

	useEffect(() => {
		loadDashboardData();
	}, [loadDashboardData]);

	const handleViewReport = (report: SubmittedReport) => {
		setSelectedReport(report);
		setIsReportModalOpen(true);
	};

	const handleCloseReportModal = () => {
		setIsReportModalOpen(false);
		setSelectedReport(null);
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('sr-RS', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	if (loading) {
		return (
			<div className='dashboard-loading'>
				<p>Učitavanje dashboard-a...</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className='dashboard-error'>
				<p>{error}</p>
				<button onClick={loadDashboardData} className='btn btn-primary'>
					Pokušaj ponovo
				</button>
			</div>
		);
	}

	if (!dashboardData) {
		return (
			<div className='dashboard-error'>
				<p>Nema dostupnih podataka</p>
			</div>
		);
	}

	const getReportStatusText = (decision: boolean | null | undefined) => {
		if (decision == null) return 'Na čekanju';
		return decision ? 'Prihvaćen' : 'Odbijen';
	};

	return (
		<div className='admin-dashboard'>
			<div className='users-header'>
				<h2>Početna stranica</h2>
				<p>Pregled aktivnosti sistema za procenu rizika</p>
			</div>

			{/* Statistics Cards */}
			<div className='stats-grid'>
				<div className='stat-card'>
					<div className='stat-icon'>📋</div>
					<div className='stat-content'>
						<h3>{dashboardData.totalSurveys}</h3>
						<p>Ukupno upitnika</p>
						<span className='stat-detail'>+{dashboardData.statistics.surveysToday} danas</span>
					</div>
				</div>

				<div className='stat-card'>
					<div className='stat-icon'>📄</div>
					<div className='stat-content'>
						<h3>{dashboardData.totalReports}</h3>
						<p>Ukupno prijava</p>
						<span className='stat-detail'>+{dashboardData.statistics.reportsToday} danas</span>
					</div>
				</div>

				<div className='stat-card high-risk'>
					<div className='stat-icon'>⚠️</div>
					<div className='stat-content'>
						<h3>{dashboardData.highRiskCount}</h3>
						<p>Visok rizik</p>
						<span className='stat-detail'>{dashboardData.statistics.potentialTraffickingCount} potencijalnih slučajeva</span>
					</div>
				</div>
			</div>

			{/* Recent Submissions */}
			<div className='dashboard-section'>
				<div className='dashboard-section-header'>
					<h2>Najnovije prijave</h2>
					<Link to='/admin/surveys' className='btn btn-primary'>
						Prikaži sve →
					</Link>
				</div>

				<div className='recent-submissions'>
					{dashboardData.recentSubmissions.length === 0 ? (
						<div className='no-data'>
							<p>Nema novih prijava</p>
						</div>
					) : (
						<div className='submissions-list'>
							{dashboardData.recentSubmissions.map((survey) => (
								<div key={survey.id} className='submission-card'>
									<div className='submission-header'>
										<div className='submission-info'>
											<span className='submission-date'>{formatDate(survey.submittedAt)}</span>
										</div>
										{/* <div className={`risk-badge ${getRiskLevelClass(survey.riskLevel)}`}>{getRiskLevelText(survey.riskLevel)}</div> */}
									</div>

									<div className='submission-details'>
										<div className='detail-item'>
											<span className='label'>Ciljana grupa:</span>
											<span className='value'>{getTargetGroupText(survey.targetGroup)}</span>
										</div>

										<div className='detail-item'>
											<span className='label'>Status:</span>
											<span className='value'>{getReportStatusText(survey?.report?.decision) ?? 'N/A'}</span>
										</div>

										<div className='detail-item'>
											<span className='label'>Rizik skor:</span>
											<span className={`value  risk-badge ${getRiskLevelClass(survey.riskLevel)}`}>{getRiskLevelText(survey.riskLevel)}</span>
										</div>

										<div className='submission-actions'>
											<Link to={`/admin/surveys/${survey.id}`} className='btn btn-primary btn-sm'>
												Prikaži detalje
											</Link>
											{survey.report && (
												<button onClick={() => handleViewReport(survey.report!)} className='btn btn-outline ' style={{ minWidth: 161.25 }}>
													Vidi prijavu
												</button>
											)}
										</div>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			{/* Report View Modal */}
			{selectedReport && <ReportViewModal isOpen={isReportModalOpen} onClose={handleCloseReportModal} reportData={selectedReport.formData} surveyId={selectedReport.surveyId} report={selectedReport} riskScore={selectedReport.riskScore} timestamp={selectedReport.submittedAt} />}
		</div>
	);
};
